import { idToken } from '../auth/firebase';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * The one place a request leaves the app. Every call carries the bearer token; nothing
 * else in the client thinks about auth.
 */
export async function api<T>(
  path: string,
  init: Omit<RequestInit, 'body'> & { body?: unknown } = {},
): Promise<T> {
  const { body, ...rest } = init;
  const token = await idToken();
  const headers = new Headers(rest.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  // `FormData` sets its own multipart Content-Type, boundary included — the browser
  // gets that wrong if we set it ourselves.
  const isForm = body instanceof FormData;
  if (body !== undefined && !isForm) headers.set('Content-Type', 'application/json');

  const res = await fetch(`/api${path}`, {
    ...rest,
    headers,
    ...(body !== undefined ? { body: isForm ? body : JSON.stringify(body) } : {}),
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const parsed: unknown = text ? JSON.parse(text) : undefined;

  if (!res.ok) {
    const body = parsed as { error?: string; details?: unknown } | undefined;
    throw new ApiError(res.status, body?.error ?? res.statusText, body?.details);
  }
  return parsed as T;
}

export const get = <T,>(path: string) => api<T>(path);
export const post = <T,>(path: string, body?: unknown) => api<T>(path, { method: 'POST', body });
export const postForm = <T,>(path: string, body: FormData) => api<T>(path, { method: 'POST', body });
export const patch = <T,>(path: string, body?: unknown) => api<T>(path, { method: 'PATCH', body });
export const put = <T,>(path: string, body?: unknown) => api<T>(path, { method: 'PUT', body });
export const del = <T,>(path: string) => api<T>(path, { method: 'DELETE' });

/** Fetches a protected file's bytes — a plain `<img src>` or `<a href>` can't carry the
 * bearer token, so this is the one place a binary fetch leaves the app outside `api()`. */
export async function fetchBlob(path: string): Promise<Blob> {
  const token = await idToken();
  const headers = new Headers();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const res = await fetch(`/api${path}`, { headers });
  if (!res.ok) throw new ApiError(res.status, res.statusText);
  return res.blob();
}

/** Fetches a protected file and hands it straight to the browser's save flow. */
export async function downloadFile(path: string, filename: string): Promise<void> {
  const url = URL.createObjectURL(await fetchBlob(path));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Opens a protected file in a new tab, e.g. a PDF in the browser's own viewer. Opens
 * the tab synchronously (before the `await`) so it counts as a direct result of the
 * click and isn't blocked as a popup; the fetched blob is what it navigates to. */
export async function openInNewTab(path: string): Promise<void> {
  const tab = window.open('', '_blank');
  const blob = await fetchBlob(path);
  if (tab) tab.location.href = URL.createObjectURL(blob);
}
